import { ReactElement, useCallback, useMemo } from 'react'
import { getPolygonNormal, SelectionBorder } from '@scene'
import { isArray, isUndefined, maxBy, minBy, toNumber } from 'lodash-es'
import { Vector3 } from 'three'
import { useTheme } from '@mui/material/styles'
import { ImmutableVector3, toImmutable } from '@modugen/ui-toolkit'
import { ThreeEvent } from '@react-three/fiber'
import { useTriggerContextMenu } from '@ui/hooks'
import { useGuidFocusTransparencyOpacity, useIfcImportSelectionActive } from '@editorHooks'
import ShapeMesh from '../ShapeMesh'

interface Props extends IntervalProps {
  interval: StiffeningSegment
  index: number
  wallPoints: ImmutableVector3[]
  pointerPropagation?: boolean
  noPointerInteractions?: boolean
  opacity?: number
  wallGuid: string
}

const thickness = 0.01

const StiffeningMeshElement = ({
  wallPoints,
  interval,
  index,
  pointerPropagation,
  noPointerInteractions,
  opacity,
  onClick = () => null,
  selectedIds = new Set(),
  wallGuid,
}: Props): ReactElement => {
  const ifcDrawer = useIfcImportSelectionActive()

  const onClickEnhanced = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      if (ifcDrawer) return
      onClick(event)
    },
    [ifcDrawer, onClick],
  )

  const { scenePalette } = useTheme()
  const typedSelectedIds = useMemo(() => {
    return isArray(selectedIds) ? new Set(selectedIds) : selectedIds
  }, [selectedIds])

  const pointsByInterval = useMemo(() => {
    const [initialStart, end] = wallPoints
    const direction = end.sub(initialStart).normalize()
    let height = 0
    if (interval.effective_height === undefined) {
      // Stiffening Proposals don't have a height yet
      const zMax = maxBy(wallPoints, 'z')?.z || 0
      const zMin = minBy(wallPoints, 'z')?.z || 0
      height = zMax - zMin
    } else {
      height = interval.effective_height
    }

    const bottomLeft = new Vector3().addVectors(
      initialStart.v,
      direction.v.multiplyScalar(toNumber(interval.interval.lower)),
    )
    const distance = toNumber(interval.interval.upper) - toNumber(interval.interval.lower)

    const bottomRight = new Vector3().addVectors(bottomLeft, direction.v.multiplyScalar(distance))
    const heightOffset = new Vector3(0, 0, height)
    const topRight = new Vector3().addVectors(bottomRight, heightOffset)
    const topLeft = new Vector3().addVectors(bottomLeft, heightOffset)
    const directedPoints = [bottomLeft, bottomRight, topRight, topLeft]

    const v1 = bottomRight.clone().sub(bottomLeft).normalize()
    const v2 = topLeft.clone().sub(bottomLeft).normalize()
    const extrusionDirection = v1.cross(v2)

    const directedPointsImmutable = directedPoints.map(p => toImmutable(p))

    return {
      points: directedPointsImmutable,
      selectable: interval.selectable === 'Selectable',
      stiffening: interval.stiffening === 'Stiffening',
      localId: interval.localId,
      guid: interval.guid,
      shapeData: {
        element_guid: interval.element_guid,
        domains: [],
        shape: {
          points: directedPointsImmutable.map(point => {
            return point.add(toImmutable(extrusionDirection).multiplyScalar(-thickness / 2))
          }),
          guid: '',
        },
        guid: '',
        thickness,
        openings: [],
        placement: undefined,
        storey: '',
      },
    }
  }, [wallPoints, interval])

  const { shapeData, selectable, stiffening, localId, guid } = pointsByInterval
  const isSelected = typedSelectedIds.has(localId as string) || typedSelectedIds.has(guid as string)
  const baseOpacity = isUndefined(opacity) ? (selectable ? 0.4 : 0) : opacity
  const meshOpacity = useGuidFocusTransparencyOpacity(guid as string, baseOpacity)

  const { openContextMenuViaEvent } = useTriggerContextMenu()

  const userData = useMemo(() => {
    const clipPlaneNormal = getPolygonNormal(shapeData.shape.points)
    const clipPlanePoint = shapeData.shape.points[0]

    return {
      localId,
      selectable,
      stiffening,
      guid,
      contextMenuActions: {
        hide: {
          guid: wallGuid,
          label: 'Verstecken (Wand)',
        },
        properties: {
          guid: guid as string,
          label: 'Eigenschaften (Aussteifungssegment)',
        },
        propertiesWall: {
          guid: wallGuid,
          label: 'Eigenschaften (Wand)',
        },
        'clip-plane': {
          normal: clipPlaneNormal,
          point: clipPlanePoint,
        },
      },
    }
  }, [guid, localId, selectable, shapeData.shape.points, stiffening, wallGuid])

  return (
    <>
      <ShapeMesh
        name={interval.guid}
        renderOrder={100 + index}
        data={shapeData}
        shapeColor={
          isSelected
            ? scenePalette.selection
            : stiffening
            ? scenePalette.elements3d.stiffening
            : undefined
        }
        applyThickness
        cursor={selectable ? 'pointer' : 'not-allowed'}
        outlines={false}
        pointerPropagation={pointerPropagation}
        noPointerInteractions={noPointerInteractions}
        onClick={onClickEnhanced}
        onPointerUp={openContextMenuViaEvent}
        userData={userData}
        meshMaterialProps={{
          // polygonOffset: true,
          // polygonOffsetUnits: -1000 + (index + 1) * 10,
          opacity: meshOpacity,
          transparent: meshOpacity < 1,
        }}
      />

      <SelectionBorder
        points={shapeData.shape.points}
        selected={isSelected}
        color={scenePalette.highlightDark}
      />
    </>
  )
}

export default StiffeningMeshElement
